// @ts-nocheck
import Events from '../constants/events';
import { siteApi } from '../services';
import {
  hasChanged,
  handleError,
  getCookie,
  setCookie,
  setItemWithExpiry,
  getItemWithExpiry,
} from '../utilities';

const defaultAllowState = {
  analytics: false,
  do_not_sell: false,
  functional: false,
  limit_pii_use: false,
  marketing: false,
  strictly_necessary: true,
  unclassified: false,
};

class ConsentState {
  constructor() {
    this.consentState = null;
    this.regionExpiration = Math.floor(12 * 30.44 * 24 * 60 * 60); // 12 months in seconds
    this.regionId = null;
    this.gcmEnabled = false;
    this.allowState = null;
    this.loading = true;
    this.defaultAllowState = defaultAllowState;
  }

  updateGoogleConsent() {
    function gtag() {
      /* eslint-disable-next-line prefer-rest-params */
      window.dataLayer.push(arguments);
    }

    const consentModeStates = {
      ad_storage: this.allowState.marketing ? 'granted' : 'denied',
      ad_user_data: this.allowState.marketing ? 'granted' : 'denied',
      ad_personalization: this.allowState.marketing ? 'granted' : 'denied',
      analytics_storage: this.allowState.analytics ? 'granted' : 'denied',
      functionality_storage: this.allowState.functional ? 'granted' : 'denied',
      personalization_storage: this.allowState.functional
        ? 'granted'
        : 'denied',
      security_storage: 'granted',
    };

    gtag('set', 'developer_id.dM2I0MW', true);
    gtag('consent', 'update', consentModeStates);
  }

  getStoredConsentState(storedState) {
    if (!storedState) return null;
    return storedState.items || storedState;
  }

  hasState(id) {
    return this.consentState.find(({ consentType }) => consentType.id === id);
  }

  computeAllowState() {
    const privacyConsents = this.consentState
      ? this.consentState.filter((rec) => rec.category === 'privacy_setting')
      : [];
    if (privacyConsents.length > 0) {
      const allowState = privacyConsents.reduce(
        (a, { subcategory, consentState: state }) => ({
          ...a,
          [subcategory]: state === 'accepted',
        }),
        {},
      );
      return { ...this.allowState, ...allowState };
    }
    return this.allowState || defaultAllowState;
  }

  syncCookie() {
    setCookie({
      name: `concord-allow-state-${this.regionId}`,
      value: JSON.stringify(this.allowState),
      maxAge: this.regionExpiration,
    });
  }

  setConsent({ regionId, regionExpiration, gcmEnabled }) {
    this.regionId = regionId;
    this.regionExpiration = regionExpiration;
    this.gcmEnabled = gcmEnabled;

    /* Try to get allow state from cookie first */
    const cookieValue = getCookie({
      name: `concord-allow-state-${this.regionId}`,
    });

    let parsedCookieValue;
    try {
      parsedCookieValue = JSON.parse(cookieValue);
      /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    } catch (e) {
      parsedCookieValue = null;
    }

    /* Try to get full consent state from localStorage */
    const storedState = getItemWithExpiry({
      key: `concord-consent-state-${this.regionId}`,
    });

    const consentState = this.getStoredConsentState(storedState);

    if (parsedCookieValue && Object.keys(parsedCookieValue).length) {
      /* Use cookie value if it exists */
      this.allowState = parsedCookieValue;
      if (
        consentState &&
        Array.isArray(consentState) &&
        consentState.length > 0
      ) {
        this.consentState = consentState;
      }
    } else if (
      consentState &&
      Array.isArray(consentState) &&
      consentState.length > 0
    ) {
      /* Use localStorage if cookie not present but localStorage exists */
      this.consentState = consentState;
      this.allowState = this.computeAllowState();
      this.syncCookie();
    } else {
      /* Use defaults if neither exists */
      this.allowState = defaultAllowState;
      this.consentState = [];
      this.syncCookie();
    }

    /* Update Google consent if enabled */
    if (this.gcmEnabled && window.dataLayer) {
      this.updateGoogleConsent();
    }
  }

  async refreshData({ forceRefresh = false } = {}) {
    try {
      /* Skip API call if we have valid local state and it isn't a force refresh */
      if (!forceRefresh && this.consentState && this.consentState.length > 0) {
        return;
      }

      this.loading = true;
      window.dispatchEvent(new CustomEvent(Events.ConsentStateLoading));

      const results = await siteApi.getConsentState(this.regionId);
      const originalData = this.consentState;
      this.consentState = results.items;

      if (results.items?.length) {
        /* Store in localStorage */
        setItemWithExpiry({
          key: `concord-consent-state-${this.regionId}`,
          value: { items: results.items },
          expirySeconds: this.regionExpiration,
        });

        if (hasChanged(results.items, originalData)) {
          const newAllowState = this.computeAllowState();
          if (hasChanged(newAllowState, this.allowState)) {
            this.allowState = newAllowState;
            this.syncCookie();
            /* Update the Google consent settings if GCM is enabled. */
            if (this.gcmEnabled && window.dataLayer) {
              this.updateGoogleConsent();
            }

            window.dispatchEvent(new CustomEvent(Events.ConsentStateChanged));
          }
        }
      }

      window.dispatchEvent(new CustomEvent(Events.ConsentStateLoaded));
    } catch (err) {
      handleError(err);
    } finally {
      this.loading = false;
    }
  }
}

export const consentState = new ConsentState();
